import { FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import {
    selectPossibleSapRatePlans,
    selectTotalMonthlyPrice,
    selectTotalMonthlyPriceWithoutRatePlan,
    selectTotalPrice,
    selectTotalPriceWithoutRatePlan,
} from '@telialt/utils/src/eshopSelectors';
import { formatNumber } from '@telialt/utils/src/helpers';
import { TeliaSegment, useSegment } from '@telialt/utils/src/segment';

import { useCartQuery } from '../../hooks/queries/useCartQuery';
import { ESHOP_TEST_ID } from '../../test/test-ids';
import { BreakdownItems, TotalPriceBlockWrapper } from './CartSummaryTotalPriceBlock.styles';
import { PriceBlock } from './PriceBlock';

type Props = {
    isStickySummary?: boolean;
};

export const CartSummaryTotalPriceBlock: FC<Props> = ({ isStickySummary }) => {
    const { cart } = useCartQuery();
    const { t } = useTranslations();
    const segment = useSegment();

    const possibleSapRatePlans = selectPossibleSapRatePlans(cart);
    const showTotalPricesWithoutRatePlan = !!possibleSapRatePlans?.length && segment !== TeliaSegment.B2b;

    const totalPriceWithoutRatePlan = selectTotalPriceWithoutRatePlan(cart);
    const totalPriceDefault = selectTotalPrice(cart);
    const totalPrice = showTotalPricesWithoutRatePlan ? totalPriceWithoutRatePlan : totalPriceDefault;

    const monthlyPriceWithoutRatePlan = selectTotalMonthlyPriceWithoutRatePlan(cart);
    const monthlyPriceDefault = selectTotalMonthlyPrice(cart);
    const monthlyPrice = showTotalPricesWithoutRatePlan ? monthlyPriceWithoutRatePlan : monthlyPriceDefault;

    const hasTotalPrice = totalPrice?.value !== undefined;
    const hasMonthlyPrice = monthlyPrice?.value > 0;

    if (!hasTotalPrice && !hasMonthlyPrice) {
        return null;
    }

    return (
        <TotalPriceBlockWrapper
            data-test={ESHOP_TEST_ID.CART_BOX_TOTAL_PRICE_BLOCK}
            $isStickySummary={isStickySummary}
            aria-label={t['eshop.product_page.cart_box.price_block.sr_label']}
        >
            {hasMonthlyPrice && (
                <BreakdownItems $isStickySummary={isStickySummary}>
                    <PriceBlock
                        priceDataTest={ESHOP_TEST_ID.CART_BOX_TOTAL_MONTHLY_PAYMENT}
                        title={t['eshop.product_page.cart_box.price_block.monthly_payment_price']}
                        price={monthlyPrice.formattedValue}
                        isStickySummary={isStickySummary}
                        ariaLabel={`${formatNumber(monthlyPrice.value)} ${t['eshop.product_page.monthly_price_unit_accessible']}`}
                    />
                </BreakdownItems>
            )}

            {hasTotalPrice && (
                <PriceBlock
                    priceDataTest={ESHOP_TEST_ID.CART_BOX_TOTAL_IMMEDIATE_PAYMENT}
                    title={t['eshop.product_page.cart_box.price_block.pay_now_price']}
                    price={totalPrice.formattedValue}
                    isLarge={true}
                    isStickySummary={isStickySummary}
                />
            )}
        </TotalPriceBlockWrapper>
    );
};
